import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { painPointApi, winApi, skillApi } from '@/services/skillKnowledgeApi';
import { useEntity, useMutation } from '@/hooks';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, Edit, Trash2 } from 'lucide-react';
import { LoadingState, ErrorState, CopyableId } from '@/components/common';
import type { PainPoint, Win, Skill } from '@/types/skillKnowledge';

export const PainPointDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAdminAuth();
  const [wins, setWins] = useState<Win[]>([]);
  const [associatedSkills, setAssociatedSkills] = useState<Skill[]>([]);

  const canEdit = hasPermission('edit', 'painPoint');
  const canDelete = hasPermission('delete', 'painPoint');

  // Use entity hook for fetching pain point
  const {
    data: painPoint,
    isLoading,
    error,
    refetch,
  } = useEntity<PainPoint>({
    fetchFn: (painPointId: string) => painPointApi.getById(painPointId),
    entityId: id,
    entityName: 'painPoint',
    enabled: !!id,
  });

  // Fetch related wins and skills when painPoint data is available
  useEffect(() => {
    if (!painPoint) return;

    // Fetch related wins if winIds exist
    if (painPoint.winIds && painPoint.winIds.length > 0) {
      Promise.allSettled(painPoint.winIds.map((winId) => winApi.getById(winId)))
        .then((winResults) => {
          const loadedWins = winResults
            .filter((result) => result.status === 'fulfilled')
            .map((result) => (result as PromiseFulfilledResult<Win>).value);
          setWins(loadedWins);
        })
        .catch((error) => {
          console.error('Error loading wins:', error);
        });
    }

    // Fetch associated skills if associatedSkills exist
    if (painPoint.associatedSkills && painPoint.associatedSkills.length > 0) {
      Promise.allSettled(
        painPoint.associatedSkills.map((skill) => skillApi.getById(skill.skillId))
      )
        .then((skillResults) => {
          const loadedSkills = skillResults
            .filter((result) => result.status === 'fulfilled')
            .map((result) => (result as PromiseFulfilledResult<Skill>).value);
          setAssociatedSkills(loadedSkills);
        })
        .catch((error) => {
          console.error('Error loading skills:', error);
        });
    }
  }, [painPoint]);

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!id) throw new Error('Pain Point ID is required');
      await painPointApi.delete(id);
    },
    entityName: 'painPoint',
    operation: 'delete',
    onSuccess: () => {
      window.location.href = '/admin/pain-points';
    },
  });

  const handleDelete = async () => {
    if (!id || !window.confirm('Are you sure you want to delete this pain point?')) return;
    await deleteMutation.mutate();
  };

  if (isLoading) {
    return <LoadingState message="Loading pain point..." />;
  }

  if (error || !painPoint) {
    return <ErrorState error={error || new Error('Pain point not found')} onRetry={refetch} />;
  }

  // Combine skills from wins and direct associations
  const allRequiredSkills = new Map<string, { skill: Skill; level: string; source: string }>();
  
  // Add skills from wins
  wins.forEach((win) => {
    win.requiredSkills?.forEach((reqSkill) => {
      const skill = associatedSkills.find((s) => s.id === reqSkill.skillId);
      if (skill) {
        allRequiredSkills.set(reqSkill.skillId, {
          skill,
          level: reqSkill.proficiencyLevel,
          source: `Win: ${win.name}`,
        });
      }
    });
  });

  // Add directly associated skills
  painPoint.associatedSkills?.forEach((assocSkill) => {
    const skill = associatedSkills.find((s) => s.id === assocSkill.skillId);
    if (skill) {
      allRequiredSkills.set(assocSkill.skillId, {
        skill,
        level: assocSkill.proficiencyLevel,
        source: 'Direct Association',
      });
    }
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/pain-points">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{painPoint.name}</h1>
            <p className="text-muted-foreground mt-2">Pain Point Details</p>
          </div>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button asChild>
              <Link to={`/admin/pain-points/${id}/edit`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit
              </Link>
            </Button>
          )}
          {canDelete && (
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isLoading}>
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <CopyableId id={painPoint.id || id || ''} label="Pain Point ID" />
            <div>
              <label className="text-sm font-medium text-muted-foreground">Name</label>
              <p className="mt-1">{painPoint.name}</p>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Description</label>
              <p className="mt-1">{painPoint.description}</p>
            </div>
            {painPoint.summary && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Summary</label>
                <p className="mt-1">{painPoint.summary}</p>
              </div>
            )}
            {painPoint.successCriteria && (
              <div>
                <label className="text-sm font-medium text-muted-foreground">Success Criteria</label>
                <p className="mt-1">{painPoint.successCriteria}</p>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-muted-foreground">Active</label>
              <p className="mt-1">
                <span
                  className={`px-2 py-1 rounded-full text-xs ${
                    painPoint.isActive
                      ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                  }`}
                >
                  {painPoint.isActive ? 'Active' : 'Inactive'}
                </span>
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Audit Information</CardTitle>
            <CardDescription>Creation and update history</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Created At</label>
                <p className="mt-1 text-sm">
                  {painPoint.createdAt ? new Date(painPoint.createdAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-muted-foreground">Updated At</label>
                <p className="mt-1 text-sm">
                  {painPoint.updatedAt ? new Date(painPoint.updatedAt).toLocaleString() : 'N/A'}
                </p>
              </div>
              {painPoint.createdBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created By</label>
                  <p className="mt-1 text-sm">{painPoint.createdBy}</p>
                </div>
              )}
              {painPoint.updatedBy && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Updated By</label>
                  <p className="mt-1 text-sm">{painPoint.updatedBy}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Related Wins */}
      {wins.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Related Wins ({wins.length})</CardTitle>
            <CardDescription>Wins associated with this pain point</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {wins.map((win) => (
                <div key={win.id} className="border rounded-lg p-4 space-y-2">
                  <Link
                    to={`/admin/wins/${win.id}`}
                    className="font-medium text-blue-600 hover:underline"
                  >
                    {win.name}
                  </Link>
                  {win.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">{win.description}</p>
                  )}
                  <div>
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        win.isActive
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200'
                      }`}
                    >
                      {win.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Associated Skills */}
      {painPoint.associatedSkills && painPoint.associatedSkills.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Associated Skills ({painPoint.associatedSkills.length})</CardTitle>
            <CardDescription>Skills directly associated with this pain point</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {painPoint.associatedSkills.map((assocSkill, index) => {
                const skill = associatedSkills.find((s) => s.id === assocSkill.skillId);
                return (
                  <div key={index} className="border rounded-lg p-4 space-y-3">
                    <Link
                      to={`/admin/skills/${assocSkill.skillId}`}
                      className="font-medium text-blue-600 hover:underline"
                    >
                      {skill?.name || 'View Skill'}
                    </Link>
                    <CopyableId id={assocSkill.skillId} label="Skill ID" />
                    {skill?.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {skill.description}
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded text-xs">
                        {assocSkill.proficiencyLevel}
                      </span>
                      {skill?.category && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-xs">
                          {skill.category.replace(/_/g, ' ')}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Combined Required Skills from Wins */}
      {allRequiredSkills.size > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Combined Required Skills ({allRequiredSkills.size})</CardTitle>
            <CardDescription>Skills from wins and direct associations</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {Array.from(allRequiredSkills.values()).map((item, index) => (
                <div key={index} className="border rounded-lg p-4 space-y-2">
                  <Link
                    to={`/admin/skills/${item.skill.id}`}
                    className="font-medium text-blue-600 hover:underline"
                  >
                    {item.skill.name}
                  </Link>
                  {item.skill.description && (
                    <p className="text-sm text-muted-foreground line-clamp-2">
                      {item.skill.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-1 bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200 rounded text-xs">
                      {item.level}
                    </span>
                    {item.skill.category && (
                      <span className="px-2 py-1 bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded text-xs">
                        {item.skill.category.replace(/_/g, ' ')}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">{item.source}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PainPointDetailPage;







